import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  EDGE_DRAG_RESISTANCE,
  SNAP_DURATION_MS,
  SNAP_EASING,
  SWIPE_THRESHOLD_PX,
} from "./welcomeScreenConstants";

export const shouldIgnoreSwipeTarget = (target) =>
  Boolean(target?.closest?.("button, a, input, textarea, select"));

const DRAG_START_THRESHOLD_PX = 8;

const applyEdgeResistance = (offset, index, slideCount) => {
  if (index === 0 && offset > 0) {
    return offset * EDGE_DRAG_RESISTANCE;
  }

  if (index === slideCount - 1 && offset < 0) {
    return offset * EDGE_DRAG_RESISTANCE;
  }

  return offset;
};

export const useWelcomeSwipe = ({ slides, activeIndex, goToSlide, containerRef }) => {
  const pointerStartX = useRef(null);
  const isTrackingSwipe = useRef(false);
  const activeIndexRef = useRef(activeIndex);
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [slideWidth, setSlideWidth] = useState(0);
  const slideCount = slides.length;

  const setActiveIndexRef = useCallback((index) => {
    activeIndexRef.current = index;
  }, []);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    slides.forEach((item) => {
      const image = new Image();
      image.src = item.heroImage;
    });
  }, [slides]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const updateSlideWidth = () => {
      setSlideWidth(container.offsetWidth);
    };

    updateSlideWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateSlideWidth);
      observer.observe(container);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateSlideWidth);
    return () => window.removeEventListener("resize", updateSlideWidth);
  }, [containerRef]);

  const resetSwipe = useCallback(() => {
    pointerStartX.current = null;
    isTrackingSwipe.current = false;
    setIsDragging(false);
    setDragOffsetX(0);
  }, []);

  const handlePointerDown = useCallback((event) => {
    if (shouldIgnoreSwipeTarget(event.target)) {
      return;
    }

    pointerStartX.current = event.clientX;
    isTrackingSwipe.current = true;

    if (event.pointerType !== "mouse" && event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      if (!isTrackingSwipe.current || pointerStartX.current === null) {
        return;
      }

      const deltaX = event.clientX - pointerStartX.current;

      if (!isDragging && Math.abs(deltaX) < DRAG_START_THRESHOLD_PX) {
        return;
      }

      setIsDragging(true);
      setDragOffsetX(
        applyEdgeResistance(deltaX, activeIndexRef.current, slideCount),
      );
    },
    [isDragging, slideCount],
  );

  const handlePointerUp = useCallback(
    (event) => {
      if (!isTrackingSwipe.current || pointerStartX.current === null) {
        resetSwipe();
        return;
      }

      const deltaX = event.clientX - pointerStartX.current;
      const index = activeIndexRef.current;
      let nextIndex = index;

      if (Math.abs(deltaX) >= SWIPE_THRESHOLD_PX) {
        if (deltaX < 0 && index < slideCount - 1) {
          nextIndex = index + 1;
        } else if (deltaX > 0 && index > 0) {
          nextIndex = index - 1;
        }
      }

      pointerStartX.current = null;
      isTrackingSwipe.current = false;
      setIsDragging(false);

      if (nextIndex !== index) {
        goToSlide(nextIndex);
      }

      setDragOffsetX(0);
    },
    [goToSlide, resetSwipe, slideCount],
  );

  const handlePointerCancel = useCallback(() => {
    resetSwipe();
  }, [resetSwipe]);

  const trackStyle = useMemo(() => {
    const slideOffsetPercent = slideCount > 0 ? (100 / slideCount) * activeIndex : 0;
    const transform = slideWidth
      ? `translate3d(${-(activeIndex * slideWidth) + dragOffsetX}px, 0, 0)`
      : `translate3d(calc(-${slideOffsetPercent}% + ${dragOffsetX}px), 0, 0)`;

    return {
      transform,
      transition: isDragging
        ? "none"
        : `transform ${SNAP_DURATION_MS}ms ${SNAP_EASING}`,
    };
  }, [activeIndex, dragOffsetX, isDragging, slideCount, slideWidth]);

  return {
    setActiveIndexRef,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    trackStyle,
    isDragging,
    slideWidth,
  };
};
